import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Briefcase, Plus } from 'lucide-react';
import { crmApi, CRM_ACTIVITY_LABELS } from '@/entities/crm-entry';
import type { CrmEntry, CrmStage } from '@/entities/crm-entry';
import { UserAvatar } from '@/entities/user';
import { QUERY_KEYS } from '@/shared/config/constants';
import { CrmKanbanCard, EmptyState, PageHeader, Sheet, Skeleton } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { formatRelative } from '@/shared/lib/format';

// Ordem dos estagios no kanban. Cores vem do STAGE_COLORS (fonte unica).
import { STAGE_COLORS } from '@/shared/ui/crm-stage-colors';

const STAGE_ORDER: ReadonlyArray<CrmStage> = [
  'LEAD',
  'EM_CONVERSA',
  'PROPOSTA',
  'FECHADO',
];

const STAGES = STAGE_ORDER.map((id) => ({
  id,
  label: STAGE_COLORS[id].label,
  dot: STAGE_COLORS[id].dot,
}));

const ACTIVITY_TIPOS = [
  { id: 'NOTA' as const, label: 'Nota' },
  { id: 'REUNIAO' as const, label: 'Reunião' },
  { id: 'VALOR_INFORMADO' as const, label: 'Valor informado' },
  { id: 'PROXIMO_PASSO' as const, label: 'Próximo passo' },
];

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);

export const CrmPage = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEYS.CRM_ENTRIES,
    queryFn: crmApi.list,
    staleTime: 60 * 1000,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activityTipo, setActivityTipo] = useState<(typeof ACTIVITY_TIPOS)[number]['id']>('NOTA');
  const [activityDesc, setActivityDesc] = useState('');

  const detail = useQuery({
    queryKey: ['crm', 'entry', selectedId],
    queryFn: () => crmApi.getById(selectedId ?? ''),
    enabled: !!selectedId,
    staleTime: 0,
  });

  const updateStageMutation = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: CrmStage }) =>
      crmApi.updateStage(id, stage),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.CRM_ENTRIES });
      void qc.invalidateQueries({ queryKey: ['crm', 'entry', selectedId] });
      toast.success('Etapa atualizada');
    },
    onError: () => toast.error('Não foi possível mover. Tente novamente.'),
  });

  const addActivityMutation = useMutation({
    mutationFn: ({
      id,
      tipo,
      descricao,
    }: {
      id: string;
      tipo: (typeof ACTIVITY_TIPOS)[number]['id'];
      descricao: string;
    }) => crmApi.addActivity(id, { tipo, descricao }),
    onSuccess: () => {
      setActivityDesc('');
      void qc.invalidateQueries({ queryKey: ['crm', 'entry', selectedId] });
      toast.success('Atividade registrada');
    },
    onError: () => toast.error('Não foi possível registrar a atividade.'),
  });

  const byStage = useMemo(() => {
    const map: Record<CrmStage, CrmEntry[]> = {
      LEAD: [],
      EM_CONVERSA: [],
      PROPOSTA: [],
      FECHADO: [],
    };
    for (const e of data?.entries ?? []) {
      map[e.stage].push(e);
    }
    return map;
  }, [data]);

  if (isLoading) {
    return (
      <section className="flex w-full flex-col gap-5" aria-busy="true" aria-label="Carregando CRM">
        <PageHeader
          title="Meu CRM"
          subtitle="Acompanhe a evolução de cada relacionamento construído no evento."
        />
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-4 lg:gap-4">
          {STAGES.map((stage, colIdx) => (
            <div key={stage.id} className="flex flex-col gap-2">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <span className={cn('h-2 w-2 rounded-full', stage.dot)} aria-hidden />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-4 w-5" />
              </div>
              <div className="flex flex-col gap-2">
                {Array.from({ length: colIdx === 0 ? 3 : 1 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex flex-col gap-2 rounded-xl bg-white p-3"
                  >
                    <div className="flex items-center gap-2">
                      <Skeleton className="size-8 shrink-0 rounded-full" />
                      <div className="flex flex-1 flex-col gap-1.5">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-2.5 w-16" />
                      </div>
                    </div>
                    <Skeleton className="h-2.5 w-3/4" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  const total = (data?.entries ?? []).length;
  const selected = detail.data?.entry ?? null;

  return (
    <section className="flex w-full flex-col gap-5">
      <PageHeader
        title="Meu CRM"
        subtitle="Acompanhe a evolução de cada relacionamento construído no evento."
      />

      {total === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="Você ainda não tem registros no CRM"
          description="Os contatos do evento aparecem aqui automaticamente."
        />
      ) : (
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-4 lg:gap-4">
          {STAGES.map((stage) => {
            const items = byStage[stage.id];
            return (
              <div key={stage.id} className="flex flex-col gap-2">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <span className={cn('h-2 w-2 rounded-full', stage.dot)} aria-hidden />
                    <p className="text-sm font-medium text-gray-900">{stage.label}</p>
                  </div>
                  <span className="text-xs font-medium text-gray-500">{items.length}</span>
                </div>
                <div className="flex flex-col gap-2">
                  {items.length === 0 ? (
                    <p className="px-1 py-2 text-xs text-gray-400">Sem contatos</p>
                  ) : (
                    items.map((e) => (
                      <CrmKanbanCard key={e.id} entry={e} onClick={() => setSelectedId(e.id)} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Sheet
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        title={selected?.participanteNome}
        description={
          selected ? `Etapa: ${STAGE_COLORS[selected.stage].label}` : undefined
        }
      >
        {detail.isLoading || !selected ? (
          <div className="flex flex-col gap-5" aria-busy="true" aria-label="Carregando detalhes">
            <div className="flex items-center gap-3">
              <Skeleton className="size-14 shrink-0 rounded-full" />
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-16 w-full rounded-lg" />
            <div className="flex flex-col gap-2">
              <Skeleton className="h-3 w-20" />
              <div className="grid grid-cols-2 gap-2">
                <Skeleton className="h-10 rounded-lg" />
                <Skeleton className="h-10 rounded-lg" />
                <Skeleton className="h-10 rounded-lg" />
                <Skeleton className="h-10 rounded-lg" />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-20 w-full rounded-lg" />
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <UserAvatar
                nome={selected.participanteNome}
                fotoUrl={selected.participanteFotoUrl ?? null}
                size="lg"
              />
              <div>
                <p className="text-lg font-bold tracking-tight text-gray-900">
                  {selected.participanteNome}
                </p>
                <p className="text-xs text-gray-500">
                  Atualizado {formatRelative(selected.updatedAt)}
                </p>
              </div>
            </div>

            {selected.valorPotencial != null ? (
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Valor potencial
                </p>
                <p className="mt-1 text-2xl font-bold tracking-tight text-gray-900">
                  {formatBRL(selected.valorPotencial)}
                </p>
              </div>
            ) : null}

            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Mover para
              </p>
              <div className="grid grid-cols-2 gap-2">
                {STAGES.map((s) => {
                  const isCurrent = s.id === selected.stage;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      disabled={isCurrent || updateStageMutation.isPending}
                      onClick={() =>
                        updateStageMutation.mutate({ id: selected.id, stage: s.id })
                      }
                      className={cn(
                        'h-12 rounded-xl border text-sm font-medium transition-colors',
                        isCurrent
                          ? 'cursor-default border-gray-900 bg-gray-50 text-gray-900'
                          : 'border-gray-200 bg-white text-gray-900 hover:border-gray-300 hover:bg-gray-50',
                        updateStageMutation.isPending && 'opacity-60',
                      )}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Registrar atividade
              </p>
              <div className="flex flex-col gap-3">
                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-hide">
                  {ACTIVITY_TIPOS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setActivityTipo(t.id)}
                      className={cn(
                        'h-9 shrink-0 rounded-full border px-3.5 text-sm font-medium transition-colors',
                        activityTipo === t.id
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <textarea
                  rows={3}
                  value={activityDesc}
                  onChange={(e) => setActivityDesc(e.target.value)}
                  placeholder="O que aconteceu nessa interação?"
                  className="min-h-[80px] resize-y rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm leading-relaxed text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
                <button
                  type="button"
                  disabled={
                    activityDesc.trim().length === 0 || addActivityMutation.isPending
                  }
                  onClick={() =>
                    addActivityMutation.mutate({
                      id: selected.id,
                      tipo: activityTipo,
                      descricao: activityDesc.trim(),
                    })
                  }
                  className="inline-flex h-11 items-center justify-center gap-2 self-start rounded-full bg-gray-900 px-5 text-sm font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  {addActivityMutation.isPending ? 'Salvando…' : 'Adicionar'}
                </button>
              </div>
            </div>

            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Histórico ({selected.atividades.length})
              </p>
              {selected.atividades.length === 0 ? (
                <p className="text-sm text-gray-500">Sem atividades registradas.</p>
              ) : (
                <ul className="space-y-2">
                  {selected.atividades.map((a) => (
                    <li
                      key={a.id}
                      className="rounded-xl border border-gray-100 bg-white p-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
                    >
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="font-semibold uppercase tracking-wider text-gray-500">
                          {CRM_ACTIVITY_LABELS[a.tipo] ?? a.tipo}
                        </span>
                        <span className="text-gray-400">{formatRelative(a.createdAt)}</span>
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-gray-700">{a.descricao}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Sheet>
    </section>
  );
};
